/* eslint-disable react/no-unescaped-entities */
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import parse from "date-fns/parse";
import { useIntl } from "react-intl";
import MailTo from "../components/errors/MailTo";
import PolicyPage from "../components/policies/PolicyPage";

function PrivacyPolicy() {
  const intl = useIntl();
  const contact = <MailTo address="contato@fundamentei.com" />;
  if (!intl.locale.startsWith("pt")) {
    return (
      <>
        <FundamenteiMetadata title="Privacy Policy | Fundamentei" />
        <PolicyPage title="Privacy Policy" lastUpdatedAt={parse("2020-09-05", "yyyy-MM-dd", new Date())}>
          <div className="prose prose-gray max-w-none p-4 lg:prose-lg">
            <p>
              This Privacy Policy has been created to emphasize Fundamentei's commitment to transparency, security, and
              privacy in the collection of data and information from users. It describes how we collect and handle data
              when you navigate our websites or use any of our products/services, become our customer, or contact
              Fundamentei via email or other available communication means.
            </p>
            <p>
              This document is written in a simple and accessible manner to allow understanding of the collection,
              rectification, deletion, and handling of the data and information provided by the user, ensuring a safe
              and pleasant experience when using our services.
            </p>
            <p>
              Fundamentei reaffirms its commitment to transparency and security, always acting in accordance with
              current internal and external legislation.
            </p>
            <p>
              For better user understanding, this <strong>Privacy Policy</strong> is divided as follows:
            </p>
            <ul>
              <li>How does Fundamentei collect my data and information?</li>
              <li>What data does Fundamentei collect?</li>
              <li>Why and how does Fundamentei use my personal data?</li>
              <li>Can Fundamentei share my data with third parties?</li>
              <li>What are my rights regarding the collected data?</li>
              <li>How long is my data stored?</li>
              <li>Data security</li>
              <li>Consent</li>
              <li>Changes and modifications to the Privacy Policy</li>
              <li>Applicable law and jurisdiction</li>
              <li>Contact</li>
              <li>Legal notice</li>
            </ul>
            <h4>How does Fundamentei collect my data and information?</h4>
            <ul>
              <li>
                <p>
                  When you visit and use our website ("fundamentei.com") and/or any other Fundamentei service, some
                  information may be collected to enhance user experience and enable certain functionalities; some of
                  this information will be collected only if you provide it. Other information may be collected
                  automatically without prior notice, such as in the case of cookies.
                </p>
              </li>
              <li>
                <p>
                  By browsing our website ("fundamentei.com") or using the services provided by Fundamentei, you
                  expressly agree to the collection and processing of personal data necessary for the provision of such
                  services to you.
                </p>
              </li>
              <li>
                <p>
                  Additionally, certain data will be collected based on clicks on content sharing buttons available on
                  our website via social media, with the user being aware that Fundamentei will not have access to
                  information such as login and password, and this collection will not allow posting content on behalf
                  of the user.
                </p>
              </li>
              <li>
                <p>
                  As provided by law, Fundamentei reserves the right to collect certain data and information without the
                  user's consent, so that the data and information collected will be used for the purposes provided by
                  the respective legislation.
                </p>
              </li>
            </ul>
            <h4>What data does Fundamentei collect?</h4>
            <ul>
              <li>
                <p>
                  If the user only visits the website ("fundamentei.com"), only browsing data will be collected through
                  cookies. For example, we use cookies to identify how many times you have visited the website, remember
                  your preferences, and seek to enhance your experiences. Cookies may include third-party cookies. The
                  user is allowed to change cookie settings directly in their browser.
                </p>
              </li>
              <li>
                <p>
                  If the user interacts with Fundamentei's materials and content, in addition to browsing data, other
                  information and data provided by the user will also be collected and processed, such as email, first
                  name, last name, photo, and IP address.
                </p>
              </li>
              <li>
                <p>
                  Furthermore, Fundamentei may also receive personal data from third parties contracted for specific
                  purposes (such as credit card processing), also reserving the right to collect data and information
                  automatically and request certain data from the user for specific purposes if necessary, such as in
                  the use of products and services.
                </p>
              </li>
              <li>
                <p>
                  By accepting the terms of Fundamentei's Privacy Policy and other provisions, the user is solely
                  responsible for the accuracy of the data and information provided, agreeing to provide only truthful
                  and legitimate personal data.
                </p>
              </li>
              <li>
                <p>
                  Credit card data, when provided at the time of subscription, will not be processed on our servers and
                  stored in our database. Such information will be sent, processed, and handled directly by Stripe (a
                  service contracted for billing purposes) in such a way that it is impossible for Fundamentei to know
                  any information about your credit card (card number and security code),{" "}
                  <strong>except for the expiration date, which will be available to Fundamentei</strong>;
                </p>
              </li>
            </ul>
            <h4>Why and how does Fundamentei use my personal data?</h4>
            <ul>
              <li>
                <p>
                  Fundamentei uses users' personal data to assist in the operation, execution, enhancement,
                  understanding, and personalization of the services provided, always aiming to provide services of the
                  highest quality in the market.
                </p>
              </li>
              <li>
                <p>Below are some specific purposes for the collection and storage of data:</p>
                <ul>
                  <li>Account creation on the Fundamentei platform;</li>
                  <li>Provision and offering of services;</li>
                  <li>Improvement and enhancement of services;</li>
                  <li>Addressing requests and inquiries;</li>
                  <li>Sending notifications;</li>
                  <li>Fraud prevention and ensuring the security and integrity of information;</li>
                  <li>Collaboration and/or compliance with orders from competent authorities;</li>
                  <li>Compliance with legal, regulatory, and normative obligations;</li>
                  <li>Preparation of statistics, research, and studies relevant to Fundamentei's activities;</li>
                  <li>
                    Regular exercise of rights to prevent any liability regarding administrative and/or judicial
                    processes.
                  </li>
                </ul>
              </li>
              <li>
                <p>
                  Fundamentei is permitted to collect, use, retain, and share data for the purposes of complying with
                  applicable law or in judicial or administrative proceedings and enforcing the Terms, other documents,
                  and applicable policies. Similarly, it is allowed to detect, prevent, and seek to resolve fraudulent
                  and illegal activities or security or technical issues, as well as to protect the interests of
                  Fundamentei, users, and/or third-party partners.
                </p>
              </li>
            </ul>
            <h4>Can Fundamentei share my data with third parties?</h4>
            <ul>
              <li>
                <p>
                  As an online platform offering services/products, Fundamentei may work together with other companies
                  to provide the service. Thus, aiming for maximum quality and customer satisfaction, Fundamentei
                  reserves the right to share its users' personal data;
                </p>
              </li>
              <li>
                <p>
                  Fundamentei may share users' data and information with authorities and regulatory bodies, especially
                  in case of compliance with court orders. Similarly, data sharing may occur when expressly requested by
                  the user;
                </p>
              </li>
              <li>
                <p>
                  In keeping with the goal of transparency, below is a list of partner companies with which Fundamentei
                  reserves the right to share data:{" "}
                  <a
                    href="https://github.com/fundamentei/policies/blob/master/third-parties.md"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    click to view.
                  </a>
                </p>
              </li>
              <li>
                <p>
                  When shared, users' data will always be shared in the manner and limits provided by the laws
                  regulating the matter, ensuring the privacy of the information. Fundamentei does not rent, sell, or
                  transfer users' personal data to any individual or legal entity, except in the cases mentioned above;
                </p>
              </li>
              <li>
                <p>
                  In accordance with Brazilian and foreign legislation, Fundamentei is entitled to collect and share
                  users' information and data internationally, provided it concerns foreign partner companies as
                  outlined above.
                </p>
              </li>
            </ul>
            <h4>What are my rights regarding the collected data?</h4>
            <ul>
              <li>
                <p>
                  You have the right to request the removal of your information at any time. Send an email to "{contact}
                  " with your request. It is important to note that once some information is shared with third-party
                  services to enhance user experience and/or maintain services provided, Fundamentei has no
                  responsibility or obligation to ensure such information will be removed from those services upon
                  request;
                </p>
              </li>
              <li>
                <p>
                  The user can always choose not to disclose their data to Fundamentei, understanding that certain data
                  may be necessary for registration on our platform and/or use of some of our features and products we
                  offer;
                </p>
                <ul>
                  <li>
                    The data mentioned in this item refers to the personal data of users and those essential for the
                    provision of services and/or supply of products.
                  </li>
                </ul>
              </li>
            </ul>
            <h4>How long is my data stored?</h4>
            <p>
              Users' personal data will be stored for a sufficient and necessary period to ensure the provision of
              services and/or compliance with the purposes set forth in this document, as provided by the legislation on
              the subject.
            </p>
            <h4>Data security</h4>
            <ul>
              <li>
                <p>
                  Fundamentei uses various security measures to ensure the integrity of its information, such as
                  industry-standard information security practices, both in the collection and storage and transmission
                  of such information;
                </p>
              </li>
              <li>
                <p>
                  Similarly, Fundamentei employs market-standard security protocols to ensure secure communication
                  between users and servers, protecting users from certain malicious activities. Thus, all communication
                  is conducted with end-to-end encryption between users and our servers;
                </p>
              </li>
              <li>
                <p>
                  Users' personal data is not encrypted. However, Fundamentei implements the necessary protection and
                  security measures to prevent any malicious activity and illegal data capture. When the user registers
                  via email and password on the site, only the password will be encrypted.
                </p>
              </li>
              <li>
                <p>
                  Fundamentei informs that it does not store users' passwords when registration on the platform occurs
                  via Google or any other intermediary means (for example, social networks and third-party logins), so
                  only the first name, last name, email, and user photos will be collected at the time of registration
                  and/or login.
                </p>
              </li>
            </ul>
            <h4>Consent</h4>
            <ul>
              <li>
                <p>
                  By browsing the website, using the services, and providing personal data on the platform, the user is
                  consenting to all the terms of this Privacy Policy, accepting it in its entirety;
                </p>
              </li>
              <li>
                <p>
                  If the user wishes to register, they will expressly consent to the collection of data necessary for
                  the purposes set forth in this document and the provision of services by Fundamentei, also taking
                  responsibility for the truthfulness, legitimacy, and need for updating the data provided;
                </p>
              </li>
              <li>
                <p>
                  The user may withdraw their consent at any time by simply sending an email to {contact} with their
                  request. By withdrawing consent, the user will automatically be subject to the impossibility of
                  accessing certain functionalities of the website when consent is required for their execution.
                </p>
              </li>
            </ul>
            <h4>Changes and modifications to the Privacy Policy</h4>
            <ul>
              <li>
                <p>
                  Fundamentei is guaranteed the right to modify the Privacy Policy at any time. This measure is
                  necessary due to Fundamentei's commitment to always being in accordance with the current laws relevant
                  to the nature of its services;
                </p>
              </li>
              <li>
                <p>
                  Therefore, it is recommended to frequently and periodically access this document so that the user is
                  aware of possible changes that may occur;
                </p>
              </li>
              <li>
                <p>Modifications will take effect immediately upon publication on the platform.</p>
              </li>
            </ul>
            <h4>Applicable law and jurisdiction</h4>
            <p>
              This document is written in accordance with Brazilian legislation regulating the matter, such that
              Brazilian law will apply, and the court of Rio de Janeiro/RJ will be elected to resolve any disputes
              and/or litigation involving this document and Fundamentei, except for exceptions provided by law.
            </p>
            <h4>Contact</h4>
            <p>
              If you have questions about the collection, use, and/or handling of your data by Fundamentei, please
              contact us at contato@fundamentei.com.br for further clarification. Fundamentei values maximum
              transparency in the operation of the website ("fundamentei.com") and/or other services and products
              offered.
            </p>
            <h4>Legal notice</h4>
            <p>
              Fundamentei is not responsible for the misuse or loss of personal data to which it does not have access or
              control. We are also exempt from responsibility for the illegal and unauthorized use of this information
              as a consequence of misuse or deviation of your access credentials, negligent or malicious conduct as a
              result of acts or omissions by users or third parties authorized by the user.
            </p>
          </div>
        </PolicyPage>
      </>
    );
  }

  return (
    <>
      <FundamenteiMetadata title="Política de Privacidade | Fundamentei" />
      <PolicyPage title="Política de Privacidade" lastUpdatedAt={parse("2020-09-05", "yyyy-MM-dd", new Date())}>
        <div className="prose prose-gray max-w-none p-4 lg:prose-lg">
          <p>
            Essa Política de Privacidade foi elaborada para enfatizar o compromisso do Fundamentei com a transparência,
            a segurança e a privacidade na coleta de dados e de informações dos usuários. Ela descreve como coletamos e
            tratamos dados quando você trafega em nossos websites ou usa qualquer um de nossos produtos/serviços,
            torna-se nosso cliente ou entra em contato com o Fundamentei por email ou através de outros meios de
            comunicação disponíveis.
          </p>
          <p>
            Este documento foi redigido de forma simples e acessível a todos, de modo que seja permitida a compreensão
            acerca da coleta, retificação, exclusão e tratamento dos dados e das informações fornecidas pelo usuário, a
            fim de garantir uma experiência segura e agradável ao se utilizar dos nossos serviços.
          </p>
          <p>
            O Fundamentei ratifica seu compromisso com a transparência e segurança, sempre atuando em conformidade com a
            legislação vigente interna e externa.
          </p>
          <p>
            Visando uma melhor compreensão do usuário, a presente <strong>Política de Privacidade</strong> é dividida da
            seguinte forma:
          </p>
          <ul>
            <li>Como o Fundamentei coleta meus dados e informações?</li>
            <li>Quais dados o Fundamentei coleta?</li>
            <li>Por que e como o Fundamentei usa meus dados pessoais?</li>
            <li>O Fundamentei pode compartilhar meus dados com terceiros?</li>
            <li>Quais são os meus direitos com relação aos dados coletados?</li>
            <li>Por quanto tempo meus dados ficam armazenados?</li>
            <li>Segurança dos dados</li>
            <li>Consentimento</li>
            <li>Mudanças e alterações na Política de Privacidade</li>
            <li>Legislação aplicável e jurisdição</li>
            <li>Contato</li>
            <li>Aviso legal</li>
          </ul>
          <h4>Como o Fundamentei coleta meus dados e informações?</h4>
          <ul>
            <li>
              <p>
                Quando você visita e utiliza nosso website ("fundamentei.com") e/ou qualquer outro serviço do
                Fundamentei, algumas informações poderão ser coletadas visando melhorar a experiência do usuário e
                viabilizar determinadas funcionalidades, algumas dessas informações serão coletadas apenas se você as
                fornecer. Outras informações poderão ser coletadas de maneira automática sem notificação prévia, como no
                caso de cookies;
              </p>
            </li>
            <li>
              <p>
                Ao navegar em nosso website ("fundamentei.com") ou utilizar os serviços prestados pelo Fundamentei, você
                concorda expressamente com a coleta e tratamento de dados pessoais necessários para o fornecimento de
                tais serviços a você;
              </p>
            </li>
            <li>
              <p>
                Ainda, há determinados dados que serão coletados de acordo com os cliques em botões de compartilhamento
                de conteúdo disponíveis em nosso website por redes sociais, ficando o usuário ciente que o Fundamentei
                não terá acesso a informações como login e senha e essa coleta não permitirá a publicação de conteúdo em
                nome do usuário;
              </p>
            </li>
            <li>
              <p>
                Nos termos previstos pela legislação, o Fundamentei se resguarda do direito de coletar determinados
                dados e informações sem o consentimento do usuário, de modo que os dados e as informações coletadas
                serão utilizados para os fins previstos pela respectiva legislação.
              </p>
            </li>
          </ul>
          <h4>Quais dados o Fundamentei coleta?</h4>
          <ul>
            <li>
              <p>
                Caso o usuário apenas visite o website ("fundamentei.com"), serão coletados somente dados de navegação
                por meio de cookies. Por exemplo, usamos os cookies para identificar quantas vezes você já visitou o
                website, lembrar de suas preferências e buscar o aprimoramento de suas experiências. Os cookies podem
                incluir cookies de terceiros. É permitido ao usuário alterar as configurações de cookies diretamente em
                seu navegador.
              </p>
            </li>
            <li>
              <p>
                Se o usuário interagir com os materiais e os conteúdos do Fundamentei, além dos dados de navegação,
                outras informações e dados fornecidos pelo próprio usuário também serão coletadas e processadas, tais
                como e-mail, nome, sobrenome, foto e endereço de IP.
              </p>
            </li>
            <li>
              <p>
                Ainda, o Fundamentei também pode receber dados pessoais vindos de terceiros contratados para
                determinadas finalidades específicas (como processamento de cartões de crédito), resguardando-se também
                no direito de colher dados e informações de forma automática e de solicitar determinados dados ao
                usuário para finalidades específicas se necessário, como no caso de utilização dos produtos e serviços.
              </p>
            </li>
            <li>
              <p>
                Ao aceitar os termos da Política de Privacidade do Fundamentei e demais disposições, o usuário é o único
                responsável pela veracidade dos dados e informações fornecidos, concordando em fornecer unicamente seus
                dados pessoais verídicos e legítimos.
              </p>
            </li>
            <li>
              <p>
                Os dados do seu cartão de crédito, quando fornecidos, no ato da assinatura, não serão processados em
                nossos servidores e armazenados em nosso banco de dados. Tais informações serão enviadas, processadas e
                manipuladas diretamente pelo Stripe (serviço contratado para fins de cobrança) de tal maneira que é
                impossível o Fundamentei saber de qualquer informação sobre seu Cartão de Crédito (número do cartão e
                código de segurança), <strong>exceto a data de validade, que estará disponível ao Fundamentei</strong>;
              </p>
            </li>
          </ul>
          <h4>Por que e como o Fundamentei usa meus dados pessoais?</h4>
          <ul>
            <li>
              <p>
                O Fundamentei utiliza os dados pessoais dos usuários para auxílio na operação, execução, aprimoração,
                compreensão e personalização dos serviços prestados, sempre almejando prestar os serviços com a mais
                alta qualidade no mercado.
              </p>
            </li>
            <li>
              <p>Abaixo seguem algumas finalidades específicas da coleta e armazenamento dos dados:</p>
              <ul>
                <li>Abertura de conta na plataforma do Fundamentei;</li>
                <li>Prestação e oferecimentos dos serviços;</li>
                <li>Melhoria e aprimoramento dos serviços;</li>
                <li>Atendimento de solicitações e dúvidas;</li>
                <li>Envio de comunicados;</li>
                <li>Prevenção à fraude e garantia de segurança e integridade das informações;</li>
                <li>Colaboração e/ou cumprimento de ordens emanadas pelas autoridades competentes;</li>
                <li>Cumprimento de obrigações legais, regulamentares, normativas e entre outras;</li>
                <li>Elaboração de estatísticas, pesquisas e estudos pertinentes à atividade do Fundamentei;</li>
                <li>
                  Exercício regular de direito, a fim de prevenir qualquer responsabilidade perante processos
                  administrativos e/ou judiciais.
                </li>
              </ul>
            </li>
            <li>
              <p>
                É permitido ao Fundamentei a coleta, o uso, a retenção e o compartilhamento de dados para os fins de
                atuar conforme exigido pela legislação aplicável ou em processos judiciais ou administrativos e impor os
                Termos, demais documentos e políticas aplicáveis. Igualmente, permite-se a fim de detectar, prevenir e
                buscar resolver atividades fraudulentas e ilícitas ou questões de segurança ou técnicas, bem como com o
                específico fim de proteger os interesses do Fundamentei, dos usuários e/ou de terceiros parceiros.
              </p>
            </li>
          </ul>
          <h4>O Fundamentei pode compartilhar meus dados com terceiros?</h4>
          <ul>
            <li>
              <p>
                Por se tratar de uma plataforma online que oferece serviço/produto, o Fundamentei poderá atuar em
                conjunto com outras empresas para prestação do serviço. Assim, visando a máxima qualidade e satisfação
                do cliente, o Fundamentei se resguarda no direito de poder compartilhar os dados pessoais dos seus
                usuários;
              </p>
            </li>
            <li>
              <p>
                O Fundamentei pode compartilhar os dados e as informações dos usuários junto a autoridades e órgãos
                reguladores, principalmente em caso de cumprimento de ordem judiciais. Igualmente, pode haver
                compartilhamento de dados quando expressamente solicitado pelo usuário;
              </p>
            </li>
            <li>
              <p>
                Cumprindo o objetivo de transparência, segue abaixo lista de empresas parceiros que o Fundamentei se
                resguarda no direito de realizar o compartilhamento de dados:{" "}
                <a
                  href="https://github.com/fundamentei/policies/blob/master/third-parties.md"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  clique para visualizar.
                </a>
              </p>
            </li>
            <li>
              <p>
                Quando efetuado, o compartilhamento dos dados dos usuários sempre se dará na forma e limites previstos
                nas legislações que regulamentam a matéria, de modo a garantir a privacidade das informações. O
                Fundamentei não aluga, vende ou transfere os dados pessoais dos usuários a qualquer pessoa física ou
                jurídica, exceto nos casos mencionados acima;
              </p>
            </li>
            <li>
              <p>
                Observando as legislações brasileiras e estrangeiras, é assegurado ao Fundamentei a coleta e
                compartilhamento de informações e de dados dos usuários internacionalmente, desde que se trate de
                empresas estrangeiras parceiras na forma exposta acima.
              </p>
            </li>
          </ul>
          <h4>Quais são os meus direitos com relação aos dados coletados?</h4>
          <ul>
            <li>
              <p>
                Você tem o direito de solicitar a remoção das suas informações a qualquer momento. Envie um email para "
                {contact}" com o seu pedido. Vale ressaltar que, uma vez que algumas informações são compartilhadas com
                serviços de terceiros visando melhorar a experiência dos usuários e/ou manutenção dos serviços
                prestados, o Fundamentei não tem a responsabilidade e/ou dever de garantir que tais informações serão
                removidas dos respectivos serviços quando solicitado;
              </p>
            </li>
            <li>
              <p>
                O usuário sempre poderá optar em não divulgar seus dados para o Fundamentei, ficando ciente que
                determinados dados podem ser necessários para registro/cadastro em nossa plataforma e/ou utilização de
                alguns de nossos recursos e produtos que oferecemos;
              </p>
              <ul>
                <li>
                  Os dados mencionados neste item fazem referência aos pessoais dos usuários e aqueles imprescindíveis à
                  prestação dos serviços e/ou fornecimento dos produtos.
                </li>
              </ul>
            </li>
          </ul>
          <h4>Por quanto tempo meus dados ficam armazenados?</h4>
          <p>
            Os dados pessoas dos usuários e visitantes serão armazenados em período suficiente e necessário a fim de
            garantir a prestação dos serviços e/ou cumprimento das finalidades previstas por este documento, conforme
            dispõe a legislação acerca do assunto.
          </p>
          <h4>Segurança dos dados</h4>
          <ul>
            <li>
              <p>
                O Fundamentei faz uso de diversos tipos de medidas de segurança para garantir a integridade de suas
                informações, como padrões de segurança de informação praticados pela indústria, tanto na coleta quanto
                no armazenamento e na transmissão de tais informações;
              </p>
            </li>
            <li>
              <p>
                Igualmente, o Fundamentei se utiliza de protocolos de segurança praticados no mercado a fim de garantir
                comunicação segura entre os usuários e os servidores, assegurando a não-exposição do usuário a
                determinadas atividades maliciosas. Assim, toda comunicação é realizada com criptografia de ponta entre
                os usuários e os nossos servidores;
              </p>
            </li>
            <li>
              <p>
                Os dados pessoais dos usuários não possuem criptografia. Contudo, o Fundamentei faz uso das medidas de
                proteção e de segurança necessárias a fim de evitar qualquer tipo de atividade maliciosa e captura
                ilegal dos dados. Quando o usuário se cadastra através do e-mail e senha no site, apenas a senha será
                criptografada.
              </p>
            </li>
            <li>
              <p>
                O Fundamentei informa que não armazena a senha dos usuários quando o cadastro na plataforma se der pelo
                Google ou qualquer outro meio intermediário (por exemplo, redes sociais e acessos terceirizados), de
                modo que somente o nome, o sobrenome, o e-mail e as fotos dos usuários serão coletados no ato do
                cadastro e/ou login.
              </p>
            </li>
          </ul>
          <h4>Consentimento</h4>
          <ul>
            <li>
              <p>
                Ao navegar pelo website, utilizar os serviços e fornecer os dados pessoais na plataforma, o usuário está
                consentindo com todos os termos desta Política de Privacidade, aceitando-a integralmente;
              </p>
            </li>
            <li>
              <p>
                Caso deseje efetuar o cadastro, o usuário estará expressamente consentindo com a coleta dos dados
                necessários para os fins previstos neste documento e consecução dos serviços prestados pelo Fundamentei,
                também se responsabilizando pela veracidade, pela legitimidade e pela necessidade de atualização dos
                dados fornecidos;
              </p>
            </li>
            <li>
              <p>
                O usuário poderá retirar seu consentimento quando bem entender, bastando enviar e-mail para {contact}{" "}
                com sua solicitação. Ao retirar o consentimento, o usuário estará automaticamente sujeito à
                impossibilidade de acessar determinadas funcionalidades do website quando o consentimento for necessário
                para suas realizações.
              </p>
            </li>
          </ul>
          <h4>Mudanças e alterações na Política de Privacidade</h4>
          <ul>
            <li>
              <p>
                Ao Fundamentei é garantida a modificação da Política de Privacidade a qualquer tempo. Esta medida se faz
                necessária em razão do comprometimento do Fundamentei de sempre estar de acordo com as legislações
                vigentes e pertinentes à natureza dos seus serviços;
              </p>
            </li>
            <li>
              <p>
                Assim, recomenda-se o acesso frequente e periódico a este documento para que o usuário esteja ciente de
                possíveis modificações que possam vir a ocorrer;
              </p>
            </li>
            <li>
              <p>As modificações realizadas terão vigência imediata após publicação na plataforma.</p>
            </li>
          </ul>
          <h4>Legislação aplicável e jurisdição</h4>
          <p>
            Este documento está redigido nos conformes previstos pela legislação brasileira que regulamenta a matéria,
            de forma que será aplicado o Direito brasileiro e elegendo-se o foro da Comarca de Rio de Janeiro/RJ para
            dirimir quaisquer controvérsias e/ou litígios envolvendo o presente documento e o Fundamentei, ressalvadas
            as exceções previstas em lei.
          </p>
          <h4>Contato</h4>
          <p>
            Caso você tenha dúvidas sobre a coleta, utilização e/ou manipulação dos seus dados pelo Fundamentei, entre
            em contato através do email contato@fundamentei.com.br para maiores esclarecimentos. O Fundamentei preza
            pela transparência máxima na operação do website ("fundamentei.com") e/ou outros serviços e produtos
            oferecidos.
          </p>
          <h4>Aviso legal</h4>
          <p>
            O Fundamentei não é responsável pelo uso indevido ou perda dos dados pessoais a que não tem acesso ou
            controle. Ficamos também isentos de responsabilidade diante do uso ilegal e não autorizado dessa informação
            como consequência de uso indevido ou desvio das suas credenciais de acesso, conduta negligente ou maliciosa
            como consequência de atos ou omissões por parte dos usuários ou de terceiros autorizados pelo usuário.
          </p>
        </div>
      </PolicyPage>
    </>
  );
}

export default PrivacyPolicy;
